import * as THREE from 'three';
import { HealthBar } from '../../HealthBar.js';
import { colored } from '../../Style.js';
import {
  NAVY, paint, foamMaterial, bake, hullGeometry, wakeGeometry, prism, sailor, HATS, Buoyancy
} from './units-style.js';

// A narrow skiff that fits within 0.95 of the 0.8 collision radius
const SKIFF = { stern: -0.6, bow: 0.7, beam: 0.62, top: 0.26, keel: -0.2, sheer: 0.14, transom: 0.6 };
const DECK_Y = 0.2;
const MAST_Z = -0.12;
// Built small, then enlarged to fill the server's 1.2 collision radius
const SCALE = 1.5;

// Harpoon Skiff (sniper): a narrow sailing skiff under a team-coloured sail,
// with a kneeling gunner behind a harpoon gun on a bow swivel
export class Sniper {
  constructor(scene, unit, color) {
    this.scene = scene;
    this.unit = unit;
    this.color = color;
    this.mesh = null;
    this.lastPosition = null;
    this.targetRotation = 0;
    this.healthBar = null;
    this.maxHealth = 15; // SniperHealth from server
    this.geometries = [];
    this.recoil = 0;
    this.create();
  }

  track(geometry) {
    this.geometries.push(geometry);
    return geometry;
  }

  part(group, parts) {
    const mesh = new THREE.Mesh(this.track(bake(parts)), paint());
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
    return mesh;
  }

  create() {
    const bodyGroup = new THREE.Group();
    const team = this.color;

    this.model = new THREE.Group();
    this.model.scale.setScalar(SCALE);
    bodyGroup.add(this.model);

    this.part(this.model, [
      hullGeometry({ ...SKIFF, side: NAVY.woodLight, trim: team, trimHeight: 0.08 }),
      // Mast and a rope coil
      colored(new THREE.CylinderGeometry(0.025, 0.035, 1.75, 5), NAVY.woodDark, { y: DECK_Y + 0.85, z: MAST_Z }),
      colored(new THREE.CylinderGeometry(0.09, 0.09, 0.05, 6), NAVY.rope, { x: 0.12, y: DECK_Y + 0.02, z: -0.42 }),
      // Kneeling gunner in a knit cap
      ...sailor({ coat: NAVY.coat, trousers: NAVY.woodDark, hat: HATS.cap, scale: 1.05, kneel: true, y: DECK_Y, z: 0.12 })
    ]);

    // Harpoon gun on a swivel at the bow, kicking back when it fires
    this.gun = new THREE.Group();
    this.gun.position.set(0, DECK_Y + 0.42, 0.33);
    this.model.add(this.gun);
    this.part(this.model, [
      colored(new THREE.CylinderGeometry(0.03, 0.04, 0.42, 5), NAVY.iron, { y: DECK_Y + 0.21, z: 0.33 })
    ]);
    this.part(this.gun, [
      colored(new THREE.BoxGeometry(0.1, 0.1, 0.36), NAVY.iron, { z: -0.02 }),
      colored(new THREE.CylinderGeometry(0.025, 0.025, 0.4, 5), NAVY.woodLight, { y: 0.07, z: 0.08, rx: Math.PI / 2 }),
      colored(new THREE.ConeGeometry(0.05, 0.12, 4), NAVY.iron, { y: 0.07, z: 0.3, rx: Math.PI / 2 }),
      colored(new THREE.BoxGeometry(0.18, 0.04, 0.04), team, { y: 0.07, z: 0.22 })
    ]);

    // Triangular sail in team colour on its own pivot so it can luff
    this.sail = new THREE.Group();
    this.sail.position.set(0, 0, MAST_Z);
    this.model.add(this.sail);
    this.part(this.sail, [
      colored(prism([[-0.02, DECK_Y + 0.42], [-0.02, DECK_Y + 1.7], [-0.46, DECK_Y + 0.45]], 0.03), team),
      colored(new THREE.BoxGeometry(0.04, 0.04, 0.46), NAVY.woodDark, { y: DECK_Y + 0.42, z: -0.23 })
    ]);

    this.water = new THREE.Group();
    this.water.scale.setScalar(SCALE);
    bodyGroup.add(this.water);
    this.wake = new THREE.Mesh(this.track(wakeGeometry({ ...SKIFF, spread: 0.08, tail: 0.1 })), foamMaterial());
    this.wake.renderOrder = 1;
    this.water.add(this.wake);

    // Create health bar
    this.healthBar = new HealthBar(this.maxHealth, 2.0, 0.25);
    this.healthBar.getGroup().position.y = 3.2;
    bodyGroup.add(this.healthBar.getGroup());

    this.mesh = bodyGroup;

    // Set initial position
    this.mesh.position.set(this.unit.position.x, this.unit.position.y, this.unit.position.z);
    this.lastPosition = { ...this.unit.position };
    this.buoyancy = new Buoyancy(this.mesh, this.model, { bob: 0.04, roll: 0.06, pitch: 0.07, topSpeed: 4 });
    this.buoyancy.update();

    this.scene.add(this.mesh);
  }

  updatePosition(position) {
    if (this.mesh) {
      // Calculate rotation based on movement direction
      if (this.lastPosition) {
        const dx = position.x - this.lastPosition.x;
        const dz = position.z - this.lastPosition.z;
        const distanceMoved = Math.sqrt(dx * dx + dz * dz);

        // Only update rotation if we've moved significantly
        if (distanceMoved > 0.01) {
          this.targetRotation = Math.atan2(dx, dz);
        }
      }

      // Smoothly interpolate rotation
      let rotationDiff = this.targetRotation - this.mesh.rotation.y;
      while (rotationDiff > Math.PI) rotationDiff -= Math.PI * 2;
      while (rotationDiff < -Math.PI) rotationDiff += Math.PI * 2;
      this.mesh.rotation.y += rotationDiff * 0.15;

      // Store last position
      this.lastPosition = { x: position.x, y: position.y, z: position.z };

      this.animate();
    }
  }

  // Floating, foam, a luffing sail and the harpoon gun's kick
  animate() {
    const dt = this.buoyancy.update();
    const speed = this.buoyancy.speed;
    this.wake.scale.set(1 + speed * 0.1, 1, 1);
    this.wake.visible = speed > 0.08;

    const t = performance.now() / 1000 + this.buoyancy.phase;
    this.sail.rotation.y = Math.sin(t * 1.3) * 0.12 * (1 - speed * 0.6);
    if (dt > 0) {
      this.recoil = Math.max(0, this.recoil - dt * 4);
      this.gun.position.z = 0.33 - 0.1 * this.recoil * this.recoil;
      this.gun.rotation.x = -0.15 * this.recoil;
    }
  }

  // Called when the harpoon gun fires
  fire() {
    this.recoil = 1;
  }

  // Set target position for aiming
  setTarget(targetPosition) {
    if (this.mesh && targetPosition) {
      const dx = targetPosition.x - this.mesh.position.x;
      const dz = targetPosition.z - this.mesh.position.z;
      this.targetRotation = Math.atan2(dx, dz);
    }
  }

  // Update health bar
  updateHealth(health, camera) {
    if (this.healthBar) {
      this.healthBar.update(health, camera);
    }
  }

  remove() {
    if (this.healthBar) {
      this.healthBar.dispose();
    }
    // Materials are shared; only geometries are ours
    this.geometries.forEach(g => g.dispose());
    this.geometries = [];
    if (this.mesh) {
      this.scene.remove(this.mesh);
    }
  }
}
